import React, { useState, useEffect, useCallback } from 'react';
import { formatUSD, formatDate } from '../utils/keyGenerator';

const TABS = [
  { id: 'overview', label: 'Gateway Overview' },
  { id: 'queue', label: 'Message Queue' },
  { id: 'gpi', label: 'GPI Tracker' },
  { id: 'compliance', label: 'Compliance Log' },
  { id: 'dispatches', label: 'Dispatch Log' },
  { id: 'audit', label: 'Audit Trail' },
];

const STATUS_COLORS = {
  SETTLED: 'badge-settled',
  ACK_RECEIVED: 'badge-settled',
  DISPATCHED: 'badge-pending',
  MESSAGE_SIGNED: 'badge-pending',
  MESSAGE_GENERATED: 'badge-pending',
  TREASURY_APPROVED: 'badge-pending',
  COMPLIANCE_PASSED: 'badge-settled',
  COMPLIANCE_REJECTED: 'badge-failed',
  TREASURY_REJECTED: 'badge-failed',
  NAK_RECEIVED: 'badge-failed',
  RETURNED: 'badge-failed',
};

export default function SwiftOperations() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [info, setInfo] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gpiSearch, setGpiSearch] = useState('');
  const [gpiResult, setGpiResult] = useState(null);
  const [queueFilter, setQueueFilter] = useState('');
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, infoRes] = await Promise.all([
        fetch('/api/swift/stats').then(r => r.ok ? r.json() : null),
        fetch('/api/swift/info').then(r => r.ok ? r.json() : null),
      ]);
      setStats(statsRes);
      setInfo(infoRes);
    } catch {}
    setLoading(false);
  }, []);

  const fetchTab = useCallback(async (t) => {
    try {
      if (t === 'queue') {
        const url = queueFilter ? `/api/swift/queue?status=${queueFilter}` : '/api/swift/queue';
        const data = await fetch(url).then(r => r.ok ? r.json() : []);
        setQueue(data);
      } else if (t === 'dispatches') {
        const data = await fetch('/api/swift/dispatches').then(r => r.ok ? r.json() : []);
        setDispatches(data);
      } else if (t === 'compliance') {
        const data = await fetch('/api/swift/compliance').then(r => r.ok ? r.json() : []);
        setCompliance(data);
      } else if (t === 'audit') {
        const data = await fetch('/api/swift/audit').then(r => r.ok ? r.json() : []);
        setAudit(data);
      }
    } catch {}
  }, [queueFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchTab(tab); }, [tab, fetchTab]);

  const handleGpiSearch = async () => {
    if (!gpiSearch.trim()) return;
    setGpiResult(null);
    try {
      // Search by UETR or message ID in the queue
      const allMsgs = await fetch('/api/swift/queue').then(r => r.ok ? r.json() : []);
      const found = allMsgs.find(m =>
        m.uetr === gpiSearch.trim() ||
        m.msgId === gpiSearch.trim() ||
        (m.payoutId && m.payoutId === gpiSearch.trim())
      );
      setGpiResult(found || { notFound: true, query: gpiSearch });
    } catch {
      setGpiResult({ notFound: true, query: gpiSearch });
    }
  };

  const handleViewMessage = async (msgId) => {
    try {
      const data = await fetch(`/api/swift/message/${msgId}`).then(r => r.ok ? r.json() : null);
      setSelectedMsg(data);
    } catch {}
  };

  const refreshAll = () => {
    fetchData();
    fetchTab(tab);
  };

  return (
    <div className="gap-16">
      {/* Gateway Status Bar */}
      <div className="settle-bar">
        <div className="settle-dot" />
        <span className="settle-text">Dwin SWIFT Gateway — {info ? 'ONLINE' : 'CONNECTING...'}</span>
        <span className="settle-time">
          {info ? `${info.bank} (${info.bic})` : ''}
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 12, padding: '2px 10px', fontSize: 10 }} onClick={refreshAll}>Refresh</button>
        </span>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '12px 8px', textAlign: 'center', cursor: 'pointer',
            background: tab === t.id ? 'var(--gold-soft)' : 'var(--surface)',
            borderRight: t.id !== 'audit' ? '1px solid var(--border)' : 'none',
            fontWeight: tab === t.id ? 700 : 500,
            color: tab === t.id ? 'var(--gold)' : 'var(--text3)',
            fontSize: 12, transition: 'all 0.15s',
          }}>
            {t.label}
          </div>
        ))}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {tab === 'overview' && (
        <div className="gap-16">
          {/* Stats Cards */}
          {stats && (
            <div className="grid4">
              <div className="stat-tile teal">
                <div className="stat-label">Total Messages</div>
                <div className="stat-value">{stats.total.messages}</div>
                <div className="stat-sub">{stats.processedUETRs} UETRs processed</div>
              </div>
              <div className="stat-tile gold">
                <div className="stat-label">Settled</div>
                <div className="stat-value stat-green">{stats.total.settled}</div>
                <div className="stat-sub">{formatUSD(stats.total.totalValue)} total value</div>
              </div>
              <div className="stat-tile coral">
                <div className="stat-label">Rejected</div>
                <div className="stat-value stat-red">{stats.total.rejected}</div>
                <div className="stat-sub">Compliance / Treasury</div>
              </div>
              <div className="stat-tile blue">
                <div className="stat-label">Audit Entries</div>
                <div className="stat-value">{stats.auditEntries}</div>
                <div className="stat-sub">Full trail</div>
              </div>
            </div>
          )}

          {/* Daily Stats */}
          {stats?.daily && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Daily Statistics — {stats.daily.date}</span>
              </div>
              <div className="card-body">
                <div className="grid4">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{stats.daily.volume}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Messages</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--teal)' }}>{formatUSD(stats.daily.value)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Total Value</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)' }}>{stats.daily.dispatched}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Dispatched</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--coral)' }}>{stats.daily.rejected}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Rejected</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Gateway Info */}
          <div className="grid2">
            {info && (
              <div className="card">
                <div className="card-header"><span className="card-title">Gateway Configuration</span></div>
                <div className="card-body">
                  {[
                    ['Bank', info.bank],
                    ['BIC/SWIFT', info.bic],
                    ['Country', info.country],
                    ['SWIFT Member', info.swiftMember ? 'Yes (Full License)' : 'No'],
                    ['ISO 20022', info.iso20022 ? 'Enabled' : 'Disabled'],
                    ['Correspondent', `${info.correspondentBank?.name} (${info.correspondentBank?.bic})`],
                    ['Operating Hours', `${info.operatingHours?.open} - ${info.operatingHours?.close} ${info.operatingHours?.timezone}`],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontWeight: 500, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {info && (
              <div className="card">
                <div className="card-header"><span className="card-title">Processing Pipeline</span></div>
                <div className="card-body">
                  {(info.pipeline || []).map((step, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-light)', alignItems: 'center' }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--gold-soft)', color: 'var(--gold)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{i + 1}</div>
                      <span style={{ fontSize: 13, color: 'var(--text2)' }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Limits & Compliance */}
          {info && (
            <div className="grid2">
              <div className="card">
                <div className="card-header"><span className="card-title">Transaction Limits</span></div>
                <div className="card-body">
                  {[
                    ['Single Transaction Max', formatUSD(info.limits?.singleTxMax)],
                    ['Daily Max', formatUSD(info.limits?.dailyMax)],
                    ['Monthly Max', formatUSD(info.limits?.monthlyMax)],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontWeight: 700, color: 'var(--navy)', fontFamily: "'IBM Plex Mono', monospace" }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Compliance Configuration</span></div>
                <div className="card-body">
                  {[
                    ['Sanctions Lists', (info.compliance?.sanctionsLists || []).join(', ')],
                    ['KYC Required', info.compliance?.kycRequired ? 'Yes' : 'No'],
                    ['AML Threshold', formatUSD(info.compliance?.amlThreshold)],
                    ['PEP Screening', info.compliance?.pep ? 'Enabled' : 'Disabled'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontWeight: 500, fontSize: 12 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== MESSAGE QUEUE TAB ===== */}
      {tab === 'queue' && (
        <div className="gap-16">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>FILTER:</span>
            {['', 'SETTLED', 'DISPATCHED', 'ACK_RECEIVED', 'COMPLIANCE_REJECTED', 'TREASURY_REJECTED'].map(s => (
              <button key={s} className={`btn btn-sm ${queueFilter === s ? 'btn-gold' : 'btn-outline'}`}
                style={{ fontSize: 10, padding: '4px 10px' }}
                onClick={() => { setQueueFilter(s); }}>
                {s || 'ALL'}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>{queue.length} messages</span>
          </div>

          <div className="card">
            <div className="card-body" style={{ padding: 0 }}>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Message ID</th>
                      <th>UETR</th>
                      <th>Beneficiary</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th style={{ width: 60 }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No messages in queue. Send a POBO SWIFT payout to see messages here.</td></tr>
                    )}
                    {queue.map((m, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{m.msgId}</td>
                        <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'var(--text3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.uetr}</td>
                        <td style={{ fontSize: 12 }}>{m.beneficiaryName}</td>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>{formatUSD(m.amount)}</td>
                        <td><span className={`badge ${STATUS_COLORS[m.status] || 'badge-pending'}`} style={{ fontSize: 10 }}>{m.status}</span></td>
                        <td style={{ fontSize: 11, color: 'var(--text3)' }}>{m.submittedAt ? formatDate(m.submittedAt) : '-'}</td>
                        <td>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => handleViewMessage(m.msgId)}>Detail</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Message Detail Modal */}
          {selectedMsg && (
            <div className="card" style={{ border: '2px solid var(--gold)' }}>
              <div className="card-header">
                <span className="card-title">Message Detail — {selectedMsg.msgId}</span>
                <button className="btn btn-outline btn-sm" onClick={() => setSelectedMsg(null)}>Close</button>
              </div>
              <div className="card-body">
                <div className="grid2">
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: 12 }}>MESSAGE INFO</div>
                    {[
                      ['Message ID', selectedMsg.msgId],
                      ['UETR', selectedMsg.uetr],
                      ['Status', selectedMsg.status],
                      ['Amount', `${selectedMsg.currency} ${selectedMsg.amount?.toFixed(2)}`],
                      ['Beneficiary', selectedMsg.beneficiaryName],
                      ['Beneficiary BIC', selectedMsg.beneficiaryBIC || '-'],
                      ['Beneficiary Account', selectedMsg.beneficiaryAccount],
                      ['Value Date', selectedMsg.valueDate],
                      ['Key Test', selectedMsg.keyTest],
                      ['Priority', selectedMsg.priority || 'NORMAL'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)', fontSize: 12 }}>
                        <span style={{ color: 'var(--text3)' }}>{k}</span>
                        <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: 12 }}>PROCESSING PIPELINE</div>
                    {(selectedMsg.pipeline || []).map((p, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-light)', alignItems: 'center' }}>
                        <span style={{ display: 'inline-flex', width: 20, height: 20, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,148,136,0.1)', color: 'var(--teal)', fontSize: 11, flexShrink: 0 }}>{'\u2713'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{p.step}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.detail}</div>
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>{p.timestamp?.slice(11, 19)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Compliance Result */}
                {selectedMsg.complianceResult && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: 8 }}>COMPLIANCE SCREENING</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <span className={`badge ${selectedMsg.complianceResult.passed ? 'badge-settled' : 'badge-failed'}`}>
                        {selectedMsg.complianceResult.passed ? 'PASSED' : 'FAILED'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>Risk Score: {selectedMsg.complianceResult.riskScore}</span>
                    </div>
                    <table>
                      <thead><tr><th>Check</th><th>Detail</th><th>Result</th></tr></thead>
                      <tbody>
                        {(selectedMsg.complianceResult.checks || []).map((c, i) => (
                          <tr key={i}>
                            <td style={{ fontSize: 11, fontWeight: 600 }}>{c.list || c.check}</td>
                            <td style={{ fontSize: 11, color: 'var(--text2)' }}>{c.target || c.note || (c.amount !== undefined ? `Amount: ${formatUSD(c.amount)}` : '-')}</td>
                            <td><span className={`badge ${c.result === 'CLEAR' || c.result === 'UNIQUE' || c.result === 'WITHIN_LIMIT' ? 'badge-settled' : 'badge-pending'}`} style={{ fontSize: 9 }}>{c.result}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Signature */}
                {selectedMsg.signature && (
                  <div style={{ marginTop: 12, padding: 12, background: 'var(--surface2)', borderRadius: 8, fontSize: 11 }}>
                    <strong>Digital Signature (HMAC-SHA256):</strong>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", marginLeft: 8, color: 'var(--teal)', wordBreak: 'break-all' }}>{selectedMsg.signature}</span>
                  </div>
                )}

                {/* SWIFT XML */}
                {selectedMsg.swiftMessage && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em' }}>ISO 20022 pacs.008</span>
                      <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => navigator.clipboard.writeText(selectedMsg.swiftMessage)}>Copy XML</button>
                    </div>
                    <div className="code-block" style={{ maxHeight: 300 }}>{selectedMsg.swiftMessage}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== GPI TRACKER TAB ===== */}
      {tab === 'gpi' && (
        <div className="gap-16">
          <div className="card">
            <div className="card-header"><span className="card-title">SWIFT GPI Tracker — UETR Lookup</span></div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Track any SWIFT payment using its <strong>UETR</strong> (Unique End-to-End Transaction Reference), <strong>Message ID</strong>, or <strong>Payout ID</strong>.
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <input className="form-input" style={{ flex: 1, fontFamily: "'IBM Plex Mono', monospace" }}
                  placeholder="Enter UETR, Message ID, or Payout ID..."
                  value={gpiSearch} onChange={e => setGpiSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGpiSearch()} />
                <button className="btn btn-gold" onClick={handleGpiSearch}>Track</button>
              </div>
            </div>
          </div>

          {gpiResult && gpiResult.notFound && (
            <div className="card" style={{ textAlign: 'center' }}>
              <div className="card-body" style={{ padding: 40 }}>
                <div style={{ fontSize: 40, color: 'var(--text3)', marginBottom: 12 }}>{'\u2717'}</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>No Message Found</div>
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                  No SWIFT message matches "<span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{gpiResult.query}</span>"
                </div>
              </div>
            </div>
          )}

          {gpiResult && !gpiResult.notFound && (
            <div className="gap-16">
              {/* GPI Status Header */}
              <div className="card" style={{ textAlign: 'center', background: 'var(--gold-soft)' }}>
                <div className="card-body" style={{ padding: 30 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.2em', marginBottom: 8 }}>GPI TRACKING STATUS</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>{gpiResult.status}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--gold)' }}>UETR: {gpiResult.uetr}</div>
                </div>
              </div>

              {/* GPI Timeline */}
              <div className="card">
                <div className="card-header"><span className="card-title">Payment Lifecycle</span></div>
                <div className="card-body">
                  {(gpiResult.pipeline || []).map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--border-light)', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--teal), var(--teal-light))',
                          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700,
                        }}>{i + 1}</div>
                        {i < gpiResult.pipeline.length - 1 && (
                          <div style={{ width: 2, height: 20, background: 'var(--border)', marginTop: 4 }} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{p.step}</div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{p.detail}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{p.timestamp}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GPI Payment Details */}
              <div className="grid2">
                <div className="card">
                  <div className="card-header"><span className="card-title">Payment Details</span></div>
                  <div className="card-body">
                    {[
                      ['Message ID', gpiResult.msgId],
                      ['UETR', gpiResult.uetr],
                      ['Amount', `${gpiResult.currency} ${gpiResult.amount?.toFixed(2)}`],
                      ['Beneficiary', gpiResult.beneficiaryName],
                      ['Beneficiary Bank', gpiResult.beneficiaryBank || '-'],
                      ['Beneficiary BIC', gpiResult.beneficiaryBIC || '-'],
                      ['Account', gpiResult.beneficiaryAccount],
                      ['Value Date', gpiResult.valueDate],
                      ['Key Test', gpiResult.keyTest],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border-light)', fontSize: 12 }}>
                        <span style={{ color: 'var(--text3)' }}>{k}</span>
                        <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card">
                  <div className="card-header"><span className="card-title">Settlement Info</span></div>
                  <div className="card-body">
                    {[
                      ['Status', gpiResult.status],
                      ['Submitted', gpiResult.submittedAt ? formatDate(gpiResult.submittedAt) : '-'],
                      ['Dispatched', gpiResult.dispatchedAt ? formatDate(gpiResult.dispatchedAt) : '-'],
                      ['ACK Received', gpiResult.ackReceivedAt ? formatDate(gpiResult.ackReceivedAt) : '-'],
                      ['Settled', gpiResult.settledAt ? formatDate(gpiResult.settledAt) : '-'],
                      ['Signature', gpiResult.signature ? gpiResult.signature.substring(0, 24) + '...' : '-'],
                      ['Output File', gpiResult.outputFile || '-'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border-light)', fontSize: 12 }}>
                        <span style={{ color: 'var(--text3)' }}>{k}</span>
                        <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{v}</span>
                      </div>
                    ))}
                    {gpiResult.treasuryApproval && (
                      <div style={{ marginTop: 10, padding: 10, background: 'var(--gold-soft)', borderRadius: 8, fontSize: 11 }}>
                        <strong>Treasury:</strong> {gpiResult.treasuryApproval.note}<br />
                        <strong>Approver:</strong> {gpiResult.treasuryApproval.approver}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== COMPLIANCE LOG TAB ===== */}
      {tab === 'compliance' && (
        <div className="gap-16">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Compliance Screening Log</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{compliance.length} records</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {compliance.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No compliance records yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Message ID</th>
                        <th>Passed</th>
                        <th>Risk Score</th>
                        <th>Checks</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compliance.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{c.msgId}</td>
                          <td>
                            <span className={`badge ${c.passed ? 'badge-settled' : 'badge-failed'}`} style={{ fontSize: 10 }}>
                              {c.passed ? 'PASSED' : 'FAILED'}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: c.riskScore > 50 ? 'var(--coral)' : c.riskScore > 0 ? 'var(--gold)' : 'var(--teal)' }}>
                            {c.riskScore}
                          </td>
                          <td style={{ fontSize: 11 }}>
                            {(c.checks || []).map((ch, j) => (
                              <span key={j} className={`badge ${ch.result === 'CLEAR' || ch.result === 'UNIQUE' || ch.result === 'WITHIN_LIMIT' ? 'badge-settled' : 'badge-pending'}`}
                                style={{ fontSize: 9, marginRight: 4, marginBottom: 2, display: 'inline-block' }}>
                                {ch.list || ch.check}: {ch.result}
                              </span>
                            ))}
                          </td>
                          <td style={{ fontSize: 11, color: 'var(--text3)' }}>{c.timestamp ? formatDate(c.timestamp) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== DISPATCH LOG TAB ===== */}
      {tab === 'dispatches' && (
        <div className="gap-16">
          <div className="card">
            <div className="card-header">
              <span className="card-title">SWIFT Dispatch Log</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{dispatches.length} dispatches</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {dispatches.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No dispatches yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Message ID</th>
                        <th>UETR</th>
                        <th>Beneficiary</th>
                        <th>Amount</th>
                        <th>Filename</th>
                        <th>BIC</th>
                        <th>Dispatched At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dispatches.map((d, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{d.msgId}</td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'var(--text3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.uetr}</td>
                          <td style={{ fontSize: 12 }}>{d.beneficiary}</td>
                          <td style={{ fontWeight: 600 }}>{formatUSD(d.amount)}</td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'var(--teal)' }}>{d.filename}</td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{d.bic}</td>
                          <td style={{ fontSize: 11, color: 'var(--text3)' }}>{d.dispatchedAt ? formatDate(d.dispatchedAt) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== AUDIT TRAIL TAB ===== */}
      {tab === 'audit' && (
        <div className="gap-16">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Full Audit Trail</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{audit.length} entries</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {audit.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No audit entries yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Action</th>
                        <th>Message ID</th>
                        <th>Detail</th>
                        <th>Operator</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.map((a, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'var(--text3)' }}>{a.id}</td>
                          <td>
                            <span className="badge badge-ktt" style={{ fontSize: 9 }}>{a.action}</span>
                          </td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{a.msgId}</td>
                          <td style={{ fontSize: 11, color: 'var(--text2)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.detail}</td>
                          <td style={{ fontWeight: 600, fontSize: 11 }}>{a.operator}</td>
                          <td style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>{a.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
